/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import { ACCOUNT_SETTINGS_PAGES } from '../../routeConfiguration';
import { propTypes } from '../../util/types';
import { ensureCurrentUser } from '../../util/data';
import { AvatarLarge, InlineTextButton, NamedLink, NotificationBadge } from '../../components';

import css from './TopbarMobileMenu.module.css';

const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUserHasListings,
    currentUser,
    notificationCount,
    onLogout,
  } = props;

  const user = ensureCurrentUser(currentUser);

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
      </span>
    );
    return (
      <div className={css.root}>
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>

          <p className={css.separatorLabel}>Menu</p>

          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('LandingPage'))}
            name="LandingPage"
          >
            <FormattedMessage id="TopbarDesktop.landingPage" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('AboutPage'))}
            name="AboutPage"
          >
            <FormattedMessage id="TopbarDesktop.AboutPage" />
          </NamedLink>

          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('HowItWorksPage'))}
            name="HowItWorksPage"
          >
            <FormattedMessage id="TopbarDesktop.HowItWorksPage" />
          </NamedLink>

          {/* <NamedLink
          className={classNames(css.navigationLink, currentPageClass('HelpAndDonatePage'))}
          name="HelpAndDonatePage"
        >
          <FormattedMessage id="TopbarDesktop.HelpAndDonatePage" />
        </NamedLink> */}
          {/* 
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('PartnersPage'))}
            name="PartnersPage"
          >
            <FormattedMessage id="TopbarDesktop.SupportersPage" />
          </NamedLink> */}

          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('FaqPage'))}
            name="FaqPage"
          >
            <FormattedMessage id="TopbarDesktop.FaqPage" />
          </NamedLink>

          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('SafetyCenterPage'))}
            name="SafetyCenterPage"
          >
            <FormattedMessage id="TopbarDesktop.SafetyCenter" />
          </NamedLink>

          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('AboutPage'))}
            name="AboutPage"
            to={{ hash: '#contact' }}
          >
            <FormattedMessage id="TopbarDesktop.contact" />
          </NamedLink>
        </div>
        <div className={css.footer}>
          <NamedLink className={css.createNewListingLink} name="NewListingPage">
            <FormattedMessage id="TopbarMobileMenu.newListingLink" />
          </NamedLink>
        </div>
      </div>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;

  return (
    <div className={css.root}>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>

        <p className={css.separatorLabel}>
          <FormattedMessage id="TopbarMobileMenu.accountLabel" />
        </p>

        <NamedLink
          className={classNames(css.inbox, currentPageClass('NewListingPage'))}
          name="NewListingPage"
        >
          <FormattedMessage id="TopbarMobileMenu.newListingLink" />
        </NamedLink>

        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
          name="ManageListingsPage"
        >
          <FormattedMessage id="TopbarDesktop.yourListingsLink" />
        </NamedLink>

        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('InboxPage'))}
          name="InboxPage"
          params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
        >
          <FormattedMessage id="TopbarMobileMenu.inboxLink" />
          {notificationCountBadge}
        </NamedLink>
        {/* <NamedLink
          className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
          name="ManageListingsPage"
        >
          <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
        </NamedLink> */}
        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
          name="ProfileSettingsPage"
        >
          <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
        </NamedLink>
        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
          name="AccountSettingsPage"
        >
          <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
        </NamedLink>

        <span className={css.navigationLink} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </span>

        <p className={css.separatorLabel}>Menü</p>

        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('LandingPage'))}
          name="LandingPage"
        >
          <FormattedMessage id="TopbarDesktop.landingPage" />
        </NamedLink>
        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('AboutPage'))}
          name="AboutPage"
        >
          <FormattedMessage id="TopbarDesktop.AboutPage" />
        </NamedLink>

        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('HowItWorksPage'))}
          name="HowItWorksPage"
        >
          <FormattedMessage id="TopbarDesktop.HowItWorksPage" />
        </NamedLink>

        {/* <NamedLink
          className={classNames(css.navigationLink, currentPageClass('HelpAndDonatePage'))}
          name="HelpAndDonatePage"
        >
          <FormattedMessage id="TopbarDesktop.HelpAndDonatePage" />
        </NamedLink> */}

        {/* <NamedLink
          className={classNames(css.navigationLink, currentPageClass('PartnersPage'))}
          name="PartnersPage"
        >
          <FormattedMessage id="TopbarDesktop.SupportersPage" />
        </NamedLink> */}

        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('FaqPage'))}
          name="FaqPage"
        >
          <FormattedMessage id="TopbarDesktop.FaqPage" />
        </NamedLink>

        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('SafetyCenterPage'))}
          name="SafetyCenterPage"
        >
          <FormattedMessage id="TopbarDesktop.SafetyCenter" />
        </NamedLink>

        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('AboutPage'))}
          name="AboutPage"
          to={{ hash: '#contact' }}
        >
          <FormattedMessage id="TopbarDesktop.contact" />
        </NamedLink>
      </div>

      <div className={css.footer}>
        <NamedLink className={css.createNewListingLink} name="NewListingPage">
          <FormattedMessage id="TopbarMobileMenu.newListingLink" />
        </NamedLink>
      </div>
    </div>
  );
};

TopbarMobileMenu.defaultProps = { currentUser: null, notificationCount: 0, currentPage: null };

const { bool, func, number, string } = PropTypes;

TopbarMobileMenu.propTypes = {
  isAuthenticated: bool.isRequired,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
};

export default TopbarMobileMenu;
